'use client';

// imports
import { useState } from 'react';
import Link from 'next/link';
import { ChevronDown, HelpCircle, MessageCircle, ArrowRight } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useInView } from '@/hooks/useInView';

// types
interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

// constants
const base = 'transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]';
const hidden = 'opacity-0 translate-y-8';
const show = 'opacity-100 translate-y-0';

// data
const tabs = ['Overview', 'Analysis', 'Accuracy', 'Assistant', 'Privacy', 'Safety'];

const faqs: FAQItem[] = [
  // Overview
  {
    category: 'Overview',
    question: 'What is Insighta AI?',
    answer:
      'Insighta is a non-invasive, AI-assisted screening system that analyzes tongue images to identify visual patterns potentially associated with diabetes-related indicators.',
  },
  {
    category: 'Overview',
    question: 'What does the app do?',
    answer:
      'The system analyzes tongue images to detect visual characteristics and generate probabilistic insights related to potential metabolic conditions.',
  },
  {
    category: 'Overview',
    question: 'Is this a medical diagnostic tool?',
    answer:
      'No. Insighta AI is a screening support system and does not provide medical diagnosis or replace professional medical advice.',
  },
  {
    category: 'Overview',
    question: 'Who is the app for?',
    answer:
      'It is designed for individuals seeking a convenient, non-invasive way to gain preliminary insights about their diabetes health status.',
  },
  {
    category: 'Overview',
    question: 'What health condition does it focus on?',
    answer:
      'The system focuses on identifying visual patterns associated with type 2 diabetes-related risk indicators.',
  },

  // Analysis
  {
    category: 'Analysis',
    question: 'How does tongue analysis work?',
    answer:
      'The system uses computer vision and deep learning models to extract and analyze visual features from tongue images.',
  },
  {
    category: 'Analysis',
    question: 'What tongue features are analyzed?',
    answer:
      'The model evaluates features such as coating patterns, surface texture, fissures, and structural characteristics.',
  },
  {
    category: 'Analysis',
    question: 'Why is the tongue used?',
    answer:
      'The tongue may reflect observable physiological changes, which can be captured and analyzed as visual patterns by the model.',
  },
  {
    category: 'Analysis',
    question: 'How long does the analysis take?',
    answer:
      'The analysis is typically completed within a few seconds, depending on system performance.',
  },
  {
    category: 'Analysis',
    question: 'What factors can affect the results?',
    answer:
      'Results may be influenced by image quality, lighting conditions, camera angle, and individual variation.',
  },

  // Accuracy
  {
    category: 'Accuracy',
    question: 'How accurate are the results?',
    answer:
      'The model achieved over 90% accuracy on the test dataset during evaluation. However, actual performance may vary in real-world use depending on image quality, capture conditions, and individual differences.',
  },
  {
    category: 'Accuracy',
    question: 'What does “High Likelihood” mean?',
    answer:
      'It indicates that the model has detected patterns that are more strongly associated with diabetes-related indicators based on its learned representations.',
  },
  {
    category: 'Accuracy',
    question: 'Does image quality affect results?',
    answer:
      'Yes. Clear images with proper lighting and positioning improve the reliability and consistency of the analysis.',
  },
  {
    category: 'Accuracy',
    question: 'Can the app make mistakes?',
    answer:
      'Yes. As with any machine learning system, predictions are not always correct and should be interpreted as supportive insights rather than definitive conclusions.',
  },
  {
    category: 'Accuracy',
    question: 'What do confidence scores mean?',
    answer:
      'Confidence scores represent the model’s estimated probability for a given prediction, indicating how strongly the input aligns with learned patterns.',
  },

  // Assistant
  {
    category: 'Assistant',
    question: 'What is Insighta AI Assistant?',
    answer:
      'Insighta AI Assistant is an AI-powered assistant that helps explain analysis results and provide general, user-friendly insights.',
  },
  {
    category: 'Assistant',
    question: 'What can I ask it?',
    answer:
      'You can ask questions about your results and general information related to the analysis.',
  },
  {
    category: 'Assistant',
    question: 'Is it a doctor?',
    answer:
      'No. The assistant is not a medical professional and does not provide clinical advice.',
  },
  {
    category: 'Assistant',
    question: 'How reliable is it?',
    answer:
      'It provides informational support based on system outputs and should not be relied on for medical decision-making.',
  },
  {
    category: 'Assistant',
    question: 'Are conversations stored?',
    answer:
      'Conversations may be stored temporarily to support interaction quality and system performance, but they are cleared after a refresh or when a new scan is initiated.',
  },

  // Privacy
  {
    category: 'Privacy',
    question: 'Is my data secure?',
    answer:
      'Yes. Data is handled using appropriate security measures and in accordance with data privacy standards.',
  },
  {
    category: 'Privacy',
    question: 'Are images stored?',
    answer:
      'Images are only stored with user consent for research and system improvement purposes.',
  },
  {
    category: 'Privacy',
    question: 'Is data shared?',
    answer:
      'No. User data is not shared with third parties without explicit consent.',
  },
  {
    category: 'Privacy',
    question: 'Can I delete my data?',
    answer:
      'Yes. Users have control over their data and can request deletion at any time.',
  },
  {
    category: 'Privacy',
    question: 'Is my data anonymous?',
    answer:
      'Data used for research is anonymized to protect user identity.',
  },

  // Safety
  {
    category: 'Safety',
    question: 'Can this replace medical tests?',
    answer:
      'No. The system is not a substitute for laboratory testing or professional medical evaluation.',
  },
  {
    category: 'Safety',
    question: 'Should I consult a doctor?',
    answer:
      'Yes. Users are encouraged to consult qualified healthcare professionals for proper diagnosis and medical advice.',
  },
  {
    category: 'Safety',
    question: 'What should I do after a high likelihood result?',
    answer:
      'You should seek professional medical evaluation for further assessment and confirmation.',
  },
  {
    category: 'Safety',
    question: 'Is the app safe?',
    answer:
      'Yes. The system is designed as a non-invasive and informational screening tool.',
  },
  {
    category: 'Safety',
    question: 'Does it provide medical advice?',
    answer:
      'No. The system provides informational insights only and does not offer medical advice.',
  },
];

// component
export default function FAQPage() {

  // hooks
  const { ref: headerRef, isVisible: headerVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  const { ref: faqRef, isVisible: faqVisible } = useInView<HTMLDivElement>({
    threshold: 0.1,
    once: true,
  });

  const { ref: ctaRef, isVisible: ctaVisible } = useInView<HTMLDivElement>({
    threshold: 0.2,
    once: true,
  });

  // state
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('Overview');

  // derived
  const filteredFaqs = faqs.filter((f) => f.category === activeTab);

  // handlers
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* hero section */}
      <section ref={headerRef} className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 pt-20 pb-24">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle, #93c5fd 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-200/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <div className={`${base} ${headerVisible ? show : hidden} inline-flex items-center gap-2 bg-white/80 border border-blue-100 rounded-full px-4 py-1.5 text-sm font-medium text-blue-600 shadow-sm mb-6`}>
            <HelpCircle size={14} />
            Frequently Asked Questions
          </div>

          <h1 className={`${base} delay-100 ${headerVisible ? show : hidden} text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-5 leading-tight`}>
            Got{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Questions?
            </span>
          </h1>

          <p className={`${base} delay-200 ${headerVisible ? show : hidden} text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed`}>
            Everything you need to know about Insighta — how it works, what to expect, and how
            your data is handled.
          </p>
        </div>
      </section>

      {/* tabs*/}
      <div
        className={`
          ${faqVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-6'}
          transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
        `}
      >
        <div className="flex justify-center gap-8 mt-10 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setOpenIndex(null);
              }}
              className={`text-sm font-semibold pb-2 ${
                activeTab === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>

        {/* faq section*/}
        <section ref={faqRef} className="pt-12 pb-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col gap-3">
              {filteredFaqs.map((faq, i) => {
                const isOpen = openIndex === i;

                return (
                <div
                  key={i}
                  className={`
                    ${faqVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-6'}
                    transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
                  `}
                  style={{
                    transitionDelay: `${100 + i * 80}ms`,
                  }}
                >
                  <div
                    className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                      isOpen
                        ? 'border-blue-200 shadow-md bg-blue-50/40'
                        : 'border-slate-100 bg-white hover:border-blue-100 hover:shadow-sm'
                    }`}
                  >
                    <button
                      onClick={() => toggle(i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span
                        className={`text-base font-semibold ${
                          isOpen ? 'text-blue-700' : 'text-slate-800'
                        }`}
                      >
                        {faq.question}
                      </span>

                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isOpen
                            ? 'bg-blue-500 text-white rotate-180'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <ChevronDown size={16} />
                      </span>
                    </button>

                    <div
                      className={`transition-all duration-300 ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      } overflow-hidden`}
                    >
                      <p className="px-6 pb-6 text-slate-500 text-sm">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      {/* cta section */}
      <section ref={ctaRef}  className="relative overflow-hidden py-16 bg-gradient-to-br from-slate-50 to-blue-50/40">

        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #93c5fd 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
          <div
            className={`
              max-w-2xl mx-auto text-center bg-white rounded-3xl border border-blue-100 shadow-sm p-10

              ${ctaVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-6'}
              transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
            `}
          >

            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-sm mx-auto mb-5">
              <MessageCircle size={24} className="text-white" />
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Still have questions?
            </h2>

            <p className="text-slate-500 text-base mb-8 leading-relaxed">
              Our support team is here to help. Reach out and we'll get back to you as soon as
              possible.
            </p>

            <Link
              href="https://forms.gle/FMNowR1DB3jeKHzt8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 
                    bg-gradient-to-r from-blue-500 to-blue-600 
                    text-white font-semibold rounded-full 
                    px-7 py-3.5 text-base text-[12px] sm:text-sm
                    shadow-lg shadow-blue-500/25
                    hover:shadow-blue-500/40
                    hover:from-blue-600 hover:to-blue-700
                    hover:scale-[1.02]
                    transition-all duration-200 active:scale-95
                    group"
            >
              <MessageCircle size={18} />
              Contact Support
              <ArrowRight size={16} />
            </Link>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}